// SpotSheet — slide-up spot detail panel
// Triggered when an angler taps a fishing spot marker on the map.

import { X, Fish, AlertTriangle, BookOpen, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { SpotData } from '@/data/spot';

interface SpotSheetProps {
  spot: SpotData;
  onClose: () => void;
}

export function SpotSheet({ spot, onClose }: SpotSheetProps) {
  const navigate = useNavigate();

  const askBlu = () => {
    const q = encodeURIComponent(
      `I'm at ${spot.name}. Best conditions are ${spot.bestConditions.flow} flow and ${spot.bestConditions.temp} water temp. What fly should I throw right now and how should I fish it?`
    );
    navigate(`/chat?q=${q}`);
  };

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 40,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      {/* Tap-to-dismiss backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
      />

      {/* Sheet */}
      <div
        style={{
          position: 'relative', zIndex: 1,
          background: '#111', borderRadius: '24px 24px 0 0',
          paddingBottom: 'max(env(safe-area-inset-bottom,16px),16px)',
          maxHeight: '75dvh', overflowY: 'auto',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#333' }} />
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 16px 12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                background: '#1a4a2e', color: '#4cd964',
                padding: '2px 8px', borderRadius: 6,
              }}>
                {spot.code}
              </span>
              <span style={{ fontSize: 11, color: '#636366' }}>{spot.waterTypeSub}</span>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{spot.name}</h2>
            <p style={{ fontSize: 12, color: '#4a7a5e', marginTop: 3 }}>{spot.bestSeason}</p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: '50%', border: 'none',
              background: '#222', color: '#8e8e93',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0, marginTop: 2,
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Target fish badges */}
        <div style={{ padding: '0 16px 14px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {spot.targetFish.map(f => (
            <span key={f} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 11, fontWeight: 500,
              background: '#1c1c1e', color: '#c8c8cc',
              border: '1px solid #2c2c2e',
              padding: '4px 10px', borderRadius: 20,
            }}>
              <Fish size={10} color="#4a7a5e" />
              {f}
            </span>
          ))}
        </div>

        {/* Conditions row */}
        <div style={{
          margin: '0 16px 14px',
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          gap: 8,
        }}>
          <CondBadge label="Flow" value={spot.bestConditions.flow} />
          <CondBadge label="Temp" value={spot.bestConditions.temp} />
          <CondBadge label="Clarity" value={spot.bestConditions.clarity} />
        </div>

        {/* Technique */}
        <Section label="Beginner Technique">
          <p style={{ fontSize: 13, color: '#aeaeb2', lineHeight: 1.6 }}>
            {spot.beginnerTechnique}
          </p>
        </Section>

        {/* Fly box */}
        <Section label="Fly Box">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {spot.flyBox.map(fly => (
              <div key={fly.fly} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#1c1c1e', borderRadius: 10,
                padding: '8px 12px',
              }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{fly.fly}</p>
                  {fly.note && <p style={{ fontSize: 11, color: '#636366', marginTop: 1 }}>{fly.note}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: '#4a7a5e',
                    background: 'rgba(74,122,94,0.15)',
                    padding: '2px 7px', borderRadius: 6,
                  }}>
                    #{fly.size}
                  </span>
                  <p style={{ fontSize: 10, color: '#636366', marginTop: 2 }}>{fly.type}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Safety note */}
        <div style={{
          margin: '0 16px 14px',
          background: 'rgba(212,90,26,0.1)', borderRadius: 12,
          border: '1px solid rgba(212,90,26,0.25)',
          padding: '12px 14px',
          display: 'flex', gap: 10,
        }}>
          <AlertTriangle size={14} color="#d45a1a" style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontSize: 12, color: '#e0896a', lineHeight: 1.5 }}>{spot.safetyNote}</p>
        </div>

        {/* History teaser */}
        <Section label="History">
          <p style={{ fontSize: 12, color: '#636366', lineHeight: 1.6 }}>
            {spot.history.slice(0, 220)}…
          </p>
        </Section>

        {/* Ask Blu CTA */}
        <div style={{ padding: '8px 16px 0' }}>
          <button
            onClick={askBlu}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 10, padding: '15px 0', borderRadius: 16, border: 'none',
              background: 'linear-gradient(135deg, #1a5c38, #1a4a2e)',
              color: '#fff', fontSize: 15, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: '0 4px 20px rgba(26,74,46,0.5)',
            }}
          >
            <MessageSquare size={16} />
            Ask Blu about this spot
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%', marginTop: 10, padding: '12px 0',
              background: 'transparent', border: 'none',
              color: '#636366', fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <BookOpen size={12} style={{ marginRight: 5, verticalAlign: 'middle' }} />
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function CondBadge({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      background: '#1c1c1e', borderRadius: 10, border: '1px solid #2c2c2e',
      padding: '8px 10px', textAlign: 'center',
    }}>
      <p style={{ fontSize: 9, fontWeight: 700, color: '#636366', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>
        {label}
      </p>
      <p style={{ fontSize: 11, fontWeight: 500, color: '#aeaeb2', lineHeight: 1.2 }}>{value}</p>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '0 16px 14px' }}>
      <p style={{
        fontSize: 10, fontWeight: 700, color: '#4a7a5e',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
      }}>
        {label}
      </p>
      {children}
    </div>
  );
}
